import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import { L, mediaUrl, useTitle } from "../lib";
import { Reveal, SampleTag } from "../ui";
import Doorways from "./Doorways";
import { groupEvents } from "./Arrangements";
import { useSite } from "../Layout";

function Portrait({ s, showSamples }) {
  const src = s.portrait_media_id ? mediaUrl(s.portrait_media_id) : null;
  return (
    <div className="relative" data-testid="home-portrait">
      {src ? (
        <figure>
          <img src={src} alt={s.portrait_caption || `Portrait of ${s.name}`} data-testid="home-portrait-image"
            className="w-full h-auto block shadow-[0_30px_60px_-30px_rgba(42,38,35,.45)]" />
          {s.portrait_caption && <figcaption className="font-lou italic text-lou-soft mt-3 text-right" data-testid="home-portrait-caption">{s.portrait_caption}</figcaption>}
        </figure>
      ) : (
        <div className="aspect-[4/5] w-full bg-gradient-to-br from-[#E8DCCB] to-[#D8C6AE] flex flex-col items-center justify-center text-center p-10">
          <SampleTag show={showSamples} />
          <p className="font-lou italic text-2xl text-lou-ink/70 mt-4">Portrait of Louise</p>
          <p className="text-sm text-lou-soft mt-2 max-w-[16rem]">The family will place her portrait here.</p>
        </div>
      )}
    </div>
  );
}

function Hero({ s, bioIntro, showSamples }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 sm:pt-20 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <Reveal className="lg:col-span-6 lg:order-2"><Portrait s={s} showSamples={showSamples} /></Reveal>
      <div className="lg:col-span-6">
        <Reveal><p className="text-xs tracking-[0.3em] uppercase text-lou-clay mb-6">In loving memory</p></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-lou font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight" data-testid="home-name">{s.name}</h1>
        </Reveal>
        {s.subtitle && <Reveal delay={0.12}><p className="font-lou italic text-xl sm:text-2xl text-lou-soft mt-4" data-testid="home-subtitle">{s.subtitle}</p></Reveal>}
        {s.life_dates && <Reveal delay={0.15}><p className="font-lou italic text-xl text-lou-soft mt-4" data-testid="home-life-dates">{s.life_dates}</p></Reveal>}
        <Reveal delay={0.2}>
          <p className="mt-8 text-base sm:text-lg leading-relaxed text-lou-soft max-w-xl whitespace-pre-line" data-testid="home-intro">
            {s.intro || bioIntro}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={L("/share")} className="lou-btn" data-testid="home-share-cta">Share a memory</Link>
            <Link to={L("/art")} className="lou-btn-ghost" data-testid="home-art-cta">Explore her art</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ServiceCard({ svc }) {
  const e = svc.events?.length ? groupEvents(svc.events)[0] : null;
  const when = e && e.days.map((d) => d.display_date?.replace(/, \d{4}$/, "")).filter(Boolean).join(" & ");
  return (
    <Reveal>
      <Link to={L("/arrangements")} data-testid="home-service-card"
        className="group mt-16 mx-6 lg:mx-auto max-w-7xl lg:px-10 block">
        <div className="border border-lou-clay/30 lou-wash-rose px-6 sm:px-10 py-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between transition-colors duration-300 group-hover:border-lou-clay">
          <div className="flex items-start gap-4">
            <CalendarDays className="text-lou-clay mt-1 shrink-0" />
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-lou-clay">Funeral and Memorial Arrangements</p>
              {e ? <p className="font-lou text-2xl mt-2">{e.name}{when ? ` · ${when}` : ""}{e.display_time ? `, ${e.display_time}` : ""}</p>
                : <p className="font-lou text-xl mt-2 text-lou-soft">Arrangements will be shared here once confirmed.</p>}
              {e?.venue && <p className="text-sm text-lou-soft mt-1">{e.venue}</p>}
            </div>
          </div>
          <span className="text-sm inline-flex items-center gap-1">View arrangements <ArrowUpRight size={16} /></span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Home() {
  const { site } = useSite();
  useTitle(null);
  if (!site) return <div className="h-[70vh]" />;
  const s = site.settings;
  return (
    <div data-testid="louise-home">
      <Hero s={s} bioIntro={site.bio_intro} showSamples={site.show_samples} />
      {site.service && <ServiceCard svc={site.service} />}
      <Doorways doors={site.doors} />
      <Reveal>
        <section className="max-w-5xl mx-6 lg:mx-auto px-6 sm:px-12 py-20 sm:py-24 text-center mt-28 sm:mt-36 lou-wash-sky" data-testid="home-invitation">
          <p className="font-lou font-light italic text-3xl sm:text-4xl lg:text-5xl leading-snug">{s.invitation}</p>
          <Link to={L("/share")} className="lou-btn mt-10" data-testid="home-invitation-cta">Add to her story</Link>
        </section>
      </Reveal>
    </div>
  );
}
